import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {Country} from '@/types'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useTranslation} from '@/translations/i18n.tsx'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {useRef, useState} from 'react'
import {useGetCountries} from '@/services/useGetCountries'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'

export const InputSelectCountry = ({
    label,
    placeholder,
    value,
    onChange,
    errorMessage
}: {
    label: string
    value?: Country | null
    onChange: (value: Country | null) => void
    placeholder?: string
    errorMessage?: string
}) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const inputCountryRef = useRef(null)
    const countriesQuery = useGetCountries(
        {
            limit: 20
        },
        {enabled: isQueryEnabled}
    )

    return (
        <ComboboxProvider setValue={countriesQuery.onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.name ?? ''}
                setValue={value => onChange(countriesQuery.remappedData.find(country => value == country.name) ?? null)}
                setOpen={isOpen => {
                    setIsQueryEnabled(isOpen)
                    countriesQuery.onResetSearch(inputCountryRef)
                }}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Label>{label}</Label>
                    <SelectTrigger
                        placeholder={placeholder}
                        fullWidth
                        value={value?.name}
                        displayValue={() => value?.name}
                        hasError={!!errorMessage}
                    />
                    <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                    <SelectComboboxPopover sameWidth>
                        <SelectComboboxInput ref={inputCountryRef} placeholder={t('commons:search_placeholder')} />
                        <SelectComboboxList>
                            {countriesQuery.isLoading ? (
                                <>
                                    {Array.from({length: 6}, (_, index) => (
                                        <Skeleton key={index} height={32} />
                                    ))}
                                </>
                            ) : countriesQuery.isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : countriesQuery.remappedData.length > 0 ? (
                                <SelectVirtualList
                                    data={countriesQuery.remappedData}
                                    style={{height: 200}}
                                    totalCount={25}
                                    overscan={100}
                                    endReached={() => countriesQuery.hasNextPage && countriesQuery.fetchNextPage()}
                                    itemContent={(itemIndex, item) => (
                                        <SelectItem hasItemCheckbox key={itemIndex} value={item.name}>
                                            {item.name}
                                        </SelectItem>
                                    )}
                                />
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
